/**
 * Shapes del contrato del BFF de auto/moto.
 * Fuente: BffSegurarse `docs/diseno/2026-09-19-auto-b2c-pipeline.md` §3.
 * Todo camelCase, ids opacos, errores con envelope `{error:{code,...}}`.
 */

export type Ramo = 'auto' | 'moto';

/* ───────────────── §3.1 bootstrap y catálogos ───────────────── */

export interface BffOrigen {
  codigo: string;
  nombre: string;
  esPartner: boolean;
  requiereSucursal: boolean;
  crmType: string;
}

export interface BffEmisionOnline {
  habilitada: boolean;
  motivo?: 'fuera_de_horario' | 'origen_sin_eol' | string;
}

export interface BffCompania {
  codigo: string;
  nombre: string;
  logoUrl?: string;
  emisionOnlineHabilitada: boolean;
  orden: number;
}

export interface BffDescripcionCobertura {
  codigoCobertura: string;
  titulo: string;
  descripcion: string;
  aclaracion?: string | null;
  /** Código de compañía; null o ausente = aplica a todas. */
  compania?: string | null;
}

export interface BffParametros {
  origen: BffOrigen;
  estilos: Record<string, unknown> | null;
  logoUrl?: string;
  emisionOnline: BffEmisionOnline;
  tipoCotizador: 'WEB' | 'EMISION' | string;
  patenteHabilitada: boolean;
  checkBot: boolean;
  companias: BffCompania[];
  descripciones: BffDescripcionCobertura[];
}

export interface BffItemCatalogo {
  id: number;
  nombre: string;
}

export interface BffVersion extends BffItemCatalogo {
  codigoInfoAuto?: string;
}

export interface BffValuacion {
  sumaAsegurada: number;
  moneda: string;
}

export interface BffVehiculoPatente {
  /** Los ids se resuelven por nombre: vienen null si no matchean el catálogo. */
  marcaId: number | null;
  marcaNombre: string;
  modeloId: number | null;
  modeloNombre: string;
  versionId: number | null;
  versionNombre: string;
  anio: number;
  esAntiguo: boolean;
}

export interface BffPatenteResponse {
  encontrado: boolean;
  vehiculo?: BffVehiculoPatente;
}

export interface BffProvincia {
  id: number;
  nombre: string;
}

export interface BffCatalogoItem {
  codigo: string;
  descripcion: string;
  /** Solo en tipos-tarjeta: compañía a la que pertenece el tipo. */
  codigoCompania?: string;
}

/* ───────────────── §3.2 contacto ───────────────── */

export interface BffContactoVehiculo {
  marcaId: number;
  modeloId: number;
  versionId: number;
  /** Segucore cotiza por nombre: los ids solos no alcanzan. */
  marcaNombre: string;
  modeloNombre: string;
  versionNombre: string;
  anio: number;
  esCeroKm: boolean;
  patente?: string;
  tieneGnc: boolean;
  sumaAsegurada?: number | null;
  /**
   * Uso del vehículo. El cotizador NO lo pregunta (el select de tipo de uso
   * recién aparece en la revisión de datos de emisión), así que el contacto
   * manda siempre `PARTICULAR`, que es el default del BFF.
   */
  tipoUso?: TipoUsoVehiculo;
  /**
   * CODIA de la versión. El `versionId` del catálogo del BFF ya ES el CODIA,
   * así que se manda el mismo valor cuando el catálogo lo resolvió.
   */
  codigoInfoAuto?: string;
}

export type TipoUsoVehiculo = 'PARTICULAR' | 'COMERCIAL' | 'TAXI_REMIS';

/**
 * Preguntas del funnel viejo. Las cuatro son `boolean | undefined`: "no
 * contestó" no es "no". Hoy el cotizador no pregunta ninguna, así que el
 * bloque viaja ausente salvo que alguien las cargue.
 */
export interface BffPreguntasContacto {
  vtv?: boolean;
  creditoCbu?: boolean;
  disponeVehiculo?: boolean;
  prendado?: boolean;
}

export interface BffContactoPersona {
  nombre: string;
  apellido: string;
  email: string;
  codigoArea: string;
  telefono: string;
  /** ISO yyyy-MM-dd. */
  fechaNacimiento: string;
  sexo: 'F' | 'M' | 'X';
  provinciaId: number;
  localidad: string;
  codigoPostal: string;
  cuandoContrata: string;
}

export interface BffContactoRequest {
  origen: string;
  tipoCotizador: string;
  landingUrl: string;
  refererUrl: string;
  esMobile: boolean;
  vehiculo: BffContactoVehiculo;
  persona: BffContactoPersona;
  preguntas?: BffPreguntasContacto;
}

/** Aviso del embudo web EOL que no frena (US 23704). Solo viene con el embudo prendido. */
export interface BffAdvertenciaEmbudo {
  codigo: string;
  mensaje?: string | null;
  campos: string[];
}

export interface BffContactoResponse {
  prospectoId: string;
  token: string;
  expiraEn: string;
  sumaAsegurada: number;
  advertencias?: BffAdvertenciaEmbudo[];
}

/* ───────────────── §3.3 precios (SSE) ───────────────── */

/** Una de las 18 coberturas normalizadas, tal como la manda el BFF dentro de cada plan. */
export interface BffCoberturaPlan {
  codigo: string;
  nombre: string;
  incluida: boolean;
  /** Texto de la limitación cuando la hay, por ejemplo "Franq. $ 950.000". */
  detalle?: string | null;
}

export interface BffPlan {
  itemId: string;
  codigoCobertura: string;
  nombre: string;
  /** El BFF la manda `null` cuando no hay (ejemplos de contrato de retomar). */
  descripcion?: string | null;
  /** Columna del cotizador. Manda sobre `tier`. */
  columna: 'A' | 'B' | 'C' | 'D' | 'E' | string;
  /** Orden de aparición dentro de la compañía. */
  orden: number;
  nroCotizacion: string;
  compania: { codigo: string; nombre: string };
  tier: 'RC' | 'TC' | 'TR' | string;
  premioMensual: number;
  primaAnual: number;
  franquicia?: string | number | null;
  sumaAsegurada?: number | null;
  esPromocional?: boolean;
  /** Precio tachado. Lo calcula el BFF; si no viene, no se muestra tachado. */
  precioTachado?: number | null;
  /**
   * Coberturas normalizadas del plan. El BFF las manda como objetos; el `string` queda sólo por
   * compatibilidad con la forma vieja del contrato.
   */
  coberturas?: Array<BffCoberturaPlan | string>;
}

export interface BffSseStarted {
  cotizacionId: string;
  companias: string[];
  /** Suma asegurada de la cotización: fallback de los planes que no la traen. */
  sumaAsegurada?: number | null;
  /** ISO con zona. Hasta cuándo valen estos precios. */
  vigenciaHasta?: string;
}

/** Referencia a la compañía tal como la manda el BFF en `result` y en cada plan. */
export interface BffCompaniaRef {
  codigo: string;
  nombre: string;
  logoUrl?: string | null;
}

export interface BffSseResult {
  /** Objeto, NO el código suelto. El evento `error` sí manda el código como string. */
  compania: BffCompaniaRef;
  planes: BffPlan[];
}

export interface BffSseError {
  compania: string;
  code: string;
}

export interface BffSseDone {
  cotizacionId: string;
  total: number;
  degraded: boolean;
}

export interface BffCotizacionSnapshot {
  cotizacionId: string;
  estado: string;
  companias: string[];
  /** Cada plan ya trae su `compania` como objeto: no se le superpone un código suelto. */
  planes: BffPlan[];
  seleccion?: { itemId: string } | null;
  vigenciaHasta?: string;
}

/* ───────────────── §3.4 selección y pasos ───────────────── */

export interface BffSeleccionResponse {
  cotizacionId: string;
  itemId: string;
  emisionOnline: BffEmisionOnline;
  advertencias?: BffAdvertenciaEmbudo[];
}

/** `POST pasos`: 202 con el embudo apagado; 200 con `advertencias` prendido. */
export interface BffPasoResponse {
  cotizacionId: string;
  paso: PasoEmision;
  actualizadoEn: string;
  advertencias?: BffAdvertenciaEmbudo[];
}

export type PasoEmision =
  | 'revision'
  | 'datos_personales'
  | 'radicacion'
  | 'vehiculo'
  | 'pago'
  /** Solo con el embudo EOL prendido (US 23704): reemplaza a `datos_personales`. */
  | 'titular';

/* ───────────────── §3.5 emisión ───────────────── */

export interface BffTelefono {
  codigoArea: string;
  numero: string;
  esCelular: boolean;
}

export interface BffDomicilio {
  calle: string;
  numero: string;
  piso?: string;
  departamento?: string;
  provinciaId: number;
  localidad: string;
  codigoPostal: string;
}

export interface BffTomador {
  tipoPersona: 'FISICA' | 'JURIDICA';
  condicionIva: string;
  tipoDocumento: string;
  numeroDocumento: string;
  cuil: string;
  nombre: string;
  apellido: string;
  razonSocial?: string | null;
  fechaNacimiento: string;
  sexo: string;
  email: string;
  telefono: BffTelefono;
  telefonoAdicional?: BffTelefono | null;
  domicilio: BffDomicilio;
  /**
   * Edad declarada. El BFF la valida contra `fechaNacimiento` con tolerancia
   * de ±1 año. No hay input: se calcula de la fecha de nacimiento.
   */
  edad?: number;
}

/** Un cilindro de GNC: marca y número grabado, los dos opcionales. */
export interface BffCilindroGnc {
  marca?: string;
  numero?: string;
}

/** Detalle de GNC (§3.5). El BFF acepta hasta 3 cilindros. */
export interface BffGnc {
  /** Marca del regulador. */
  regulador?: string;
  /** Número de serie del regulador. */
  numeroRegulador?: string;
  numeroOblea?: string;
  /** ISO yyyy-MM-dd. Futura y a menos de un año, o el BFF responde 400. */
  vencimientoOblea?: string;
  cilindros?: BffCilindroGnc[];
}

export const MAX_CILINDROS_GNC = 3;

export type TipoInspeccion =
  | 'A-DOMICILIO'
  | 'CENTROS-COMPANIA'
  | 'A-CARGO-COMPANIA'
  | string;

/** Domicilio de la inspección. Obligatorio con `tipo=A-DOMICILIO`. */
export interface BffDomicilioInspeccion {
  /** El BFF lo recibe como texto, no como número. */
  provinciaId: string;
  localidad: string;
  codigoPostal: string;
  calle: string;
  altura: string;
  /** ISO yyyy-MM-dd. */
  fecha?: string;
  /** "HH:mm". */
  horarioDesde?: string;
  horarioHasta?: string;
  codigoArea?: string;
  telefono?: string;
}

/**
 * Coordinación de la inspección (§3.5). Reemplaza al `tipoInspeccion` suelto,
 * que el BFF sigue aceptando; si vienen los dos, gana `inspeccion.tipo`.
 */
export interface BffInspeccion {
  tipo: TipoInspeccion;
  domicilio?: BffDomicilioInspeccion | null;
  centroCompania?: string | null;
  ciaCodigoArea?: string | null;
  ciaTelefono?: string | null;
}

export type TipoCuentaBancaria = 'CAJA_AHORRO' | 'CUENTA_CORRIENTE';

export interface BffVehiculoEmision {
  /** Segucore emite por nombre, igual que cotiza. */
  marcaNombre: string;
  modeloNombre: string;
  versionNombre: string;
  patente: string;
  sinPatente: boolean;
  numeroMotor: string;
  numeroChasis: string;
  tieneGnc: boolean;
  gnc?: BffGnc | null;
  tipoUso: string;
  /** Forma vieja. Se sigue mandando por compatibilidad; gana `inspeccion.tipo`. */
  tipoInspeccion?: string | null;
  inspeccion?: BffInspeccion | null;
  esCeroKm: boolean;
}

export interface BffPago {
  medio: 'TARJETA_CREDITO' | 'CBU' | 'TARJETA_DEBITO';
  tarjeta?: {
    numero: string;
    tipo: string;
    banco: string;
    titularNombre: string;
    titularApellido: string;
    vencimientoMes: number;
    vencimientoAnio: number;
  } | null;
  cbu?: {
    numero: string;
    banco: string;
    /** Obligatorio con `medio=CBU`: ausente es 400 `requerido`. */
    tipoCuenta: TipoCuentaBancaria;
  } | null;
}

export interface BffEmitirRequest {
  tomador: BffTomador;
  vehiculo: BffVehiculoEmision;
  pago: BffPago;
  aceptaTerminos: boolean;
}

export type EstadoDocumento = 'pending' | 'ready' | string;

export interface BffPolizaResponse {
  polizaId: string;
  numeroPoliza: string;
  estado: 'ACTIVA' | 'PROPUESTA' | string;
  compania: string;
  documentos: {
    certificadoCobertura: EstadoDocumento;
    frentePoliza: EstadoDocumento;
    tarjetaMercosur: EstadoDocumento;
  };
  pago: { medio: string; ultimos4: string };
}

/** Estado de la emisión, para resolver un 409 o un 504 por polling. */
export interface BffEstadoEmision {
  estado: 'none' | 'issuing' | 'issued' | 'failed' | string;
  polizaId?: string;
  numeroPoliza?: string;
  /** Segundos antes de volver a consultar. `null` = no hay reintento útil. */
  retryAfterSeconds?: number | null;
  /**
   * La emisión quedó colgada y nadie puede confirmarla desde acá (§3.5). Viene
   * con `estado:"issuing"` y `retryAfterSeconds:null`: la pantalla avisa que
   * la póliza se está confirmando y NO ofrece reintentar.
   */
  requiereAsistencia?: boolean;
}

/* ───────────────── §3.6 documentos ───────────────── */

export type TipoDocumento =
  | 'certificadoCobertura'
  | 'frentePoliza'
  | 'tarjetaMercosur';
